import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Select, DatePicker, notification } from 'antd';
import { SearchOutlined, SettingOutlined } from '@ant-design/icons';
import moment from 'moment';
import { TitleRow, ContainerRow, SearchInputStyle, ControlsDiV, AddButton } from '../styledComponents';
import CustomTypography from '../../CustomComponents/CustomTypography';
import PageTitle from '../../../Utils/PageTitle';
import TanStackUserTable from '../../CustomComponents/TanStackUserTable';
import { createBillingAdminColumns } from './columnConfig';
import { getBillingAdminJobs, getBillerAssignmentPicker, reassignJob } from '../../../Apis/Biller';
import ReassignModal from './ReassignModal';
import BulkReassignModal from './BulkReassignModal';
import BillingAdminColumnSettingsModal, { BILLING_ADMIN_DEFAULT_COLUMNS } from './BillingAdminColumnSettingsModal';
import { getColumnPreferences, saveColumnPreferences } from '../../../Apis/Clinic';

const { RangePicker } = DatePicker;
const { Option } = Select;

const TWO_MONTHS_MS = 60 * 24 * 60 * 60 * 1000;

const BillingAdmin = () => {
  const theme = useSelector(state => state.themeReducer);
  const userData = useSelector(state => state.userReducer);
  const history = useHistory();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [clearFiltersTrigger, setClearFiltersTrigger] = useState(0);

  const [dateRange, setDateRange] = useState([moment().subtract(60, 'days'), moment()]);
  const [clinicId, setClinicId] = useState(null);
  const [clinicRequired, setClinicRequired] = useState(false);

  const [billersList, setBillersList] = useState([]);
  const [groupsList, setGroupsList] = useState([]);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reassignModalVisible, setReassignModalVisible] = useState(false);
  const [reassignLoading, setReassignLoading] = useState(false);
  const [bulkModalVisible, setBulkModalVisible] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [visibleColumns, setVisibleColumns] = useState(BILLING_ADMIN_DEFAULT_COLUMNS);
  const [columnSettingsVisible, setColumnSettingsVisible] = useState(false);

  const onReassign = useCallback(job => {
    setReassignTarget(job);
    setReassignModalVisible(true);
  }, []);

  const fetchColumnPreferences = useCallback(async () => {
    try {
      const response = await getColumnPreferences('BILLER_ADMIN_JOB_LIST');
      if (response.data && response.data.result && Array.isArray(response.data.result) && response.data.result.length > 0) {
        const columnPreferences = response.data.result.reduce((acc, item) => {
          if (item.columnName && item.columnName in BILLING_ADMIN_DEFAULT_COLUMNS) {
            acc[item.columnName] = item.selected;
          }
          return acc;
        }, {});
        setVisibleColumns({
          ...BILLING_ADMIN_DEFAULT_COLUMNS,
          ...columnPreferences,
          jobNumber: true,
          actions: true
        });
      }
    } catch (error) {
      console.error('Failed to fetch column preferences:', error);
    }
  }, []);

  useEffect(() => {
    fetchColumnPreferences();
  }, [fetchColumnPreferences]);

  const handleSaveColumnSettings = useCallback(newVisibleColumns => {
    setVisibleColumns(newVisibleColumns);
    const columnPreferencesArray = Object.keys(newVisibleColumns).map(columnName => ({
      columnName,
      selected: newVisibleColumns[columnName]
    }));
    saveColumnPreferences('BILLER_ADMIN_JOB_LIST', columnPreferencesArray)
      .then(() => {
        notification.success({
          message: 'Success',
          description: 'Column preferences saved successfully'
        });
        fetchColumnPreferences();
      })
      .catch(() => {
        notification.error({
          message: 'Error',
          description: 'Failed to save column preferences. Please try again.'
        });
      });
  }, [fetchColumnPreferences]);

  const columns = useMemo(() => createBillingAdminColumns(onReassign), [onReassign]);

  // Determine billerAdmin clinics for biller role
  const billerAdminClinics = useMemo(() => {
    if (userData.userType !== 'biller') return [];
    return (userData.clinics || []).filter(c => c.billerAdmin);
  }, [userData]);

  const showClinicPicker = userData.userType === 'biller' && billerAdminClinics.length > 1;

  // Auto-select clinic for single-clinic billers
  useEffect(() => {
    if (userData.userType === 'biller' && billerAdminClinics.length === 1 && !clinicId) {
      setClinicId(billerAdminClinics[0].clinicId);
    }
  }, [billerAdminClinics]);

  useEffect(() => {
    getBillerAssignmentPicker(clinicId || undefined)
      .then(res => {
        if (res.data.success && res.data.result?.[0]) {
          const { billers = [], billerGroups = [] } = res.data.result[0];
          setBillersList(billers);
          setGroupsList(billerGroups);
        }
      })
      .catch(() => {});
  }, [clinicId]);

  const selectedJobs = useMemo(() => {
    const selectedIds = Object.keys(rowSelection).filter(id => rowSelection[id]);
    return jobs.filter(j => selectedIds.includes(j.jobId));
  }, [jobs, rowSelection]);

  const eligibleJobs = useMemo(
    () => selectedJobs.filter(j => !j.billStatus),
    [selectedJobs]
  );

  const buildPayload = useCallback(() => {
    const payload = {
      createdFrom: dateRange[0] ? dateRange[0].valueOf() : undefined,
      createdTo: dateRange[1] ? dateRange[1].valueOf() : undefined
    };
    if (clinicId) payload.clinicId = clinicId;
    return payload;
  }, [dateRange, clinicId]);

  const fetchJobs = useCallback(() => {
    setLoading(true);
    getBillingAdminJobs(buildPayload())
      .then(response => {
        setLoading(false);
        if (response.data.success) {
          const result = response.data.result || [];
          setJobs(result);
          setClinicRequired(false);
          // Keep the sidebar Billing Admin orphaned-jobs badge in sync (TRPA-8545 AC3).
          window.dispatchEvent(
            new CustomEvent('billing-admin:orphan-count', {
              detail: result.filter(j => j.billingUnroutable).length
            })
          );
        } else if (
          response.data.message?.toLowerCase().includes('clinic_id_required') ||
          response.data.message?.toLowerCase().includes('clinicid is missing') ||
          response.data.message?.toLowerCase().includes('select a clinic')
        ) {
          setClinicRequired(true);
        } else if (response.data.message?.toLowerCase().includes('access denied')) {
          notification.warning({
            message: 'Access Changed',
            description: 'Your biller admin permissions may have changed. Please refresh.'
          });
        } else {
          notification.error({ message: response.data.message || 'Failed to load jobs' });
        }
      })
      .catch(() => {
        setLoading(false);
        notification.error({ message: 'Failed to load jobs' });
      });
  }, [buildPayload]);

  useEffect(() => {
    fetchJobs();
  }, [dateRange, clinicId]);

  const handleReassignSubmit = useCallback((job, billerConfig) => {
    setReassignLoading(true);
    const payload = { jobIds: [job.jobId], billerConfig, ...(clinicId && { clinicId }) };
    reassignJob(payload, userData.sessionId)
      .then(res => {
        setReassignLoading(false);
        if (res.data.success) {
          notification.success({ message: 'Reassigned successfully' });
          setReassignModalVisible(false);
          setReassignTarget(null);
          fetchJobs();
        } else {
          notification.error({ message: res.data.message || 'Reassignment failed' });
        }
      })
      .catch(() => {
        setReassignLoading(false);
        notification.error({ message: 'Something went wrong.' });
      });
  }, [fetchJobs, clinicId, userData.sessionId]);

  const handleBulkReassignSubmit = useCallback((billerConfig) => {
    setReassignLoading(true);
    const payload = { jobIds: eligibleJobs.map(j => j.jobId), billerConfig, ...(clinicId && { clinicId }) };
    reassignJob(payload, userData.sessionId)
      .then(res => {
        setReassignLoading(false);
        if (res.data.success) {
          notification.success({
            message: `${eligibleJobs.length} job${eligibleJobs.length > 1 ? 's' : ''} reassigned successfully`
          });
          setBulkModalVisible(false);
          setRowSelection({});
          fetchJobs();
        } else {
          notification.error({ message: res.data.message || 'Reassignment failed' });
        }
      })
      .catch(() => {
        setReassignLoading(false);
        notification.error({ message: 'Something went wrong.' });
      });
  }, [fetchJobs, clinicId, userData.sessionId, eligibleJobs]);

  // AC#2: block direct URL access — allow clinicAdmin, billingAdmin, or billers/admins with BILLER_ADMIN role
  const isBillerWithAdminRole =
    (userData.userType === 'biller' || userData.userType === 'admin') &&
    (userData.clinics || []).some(c => c.billerAdmin);
  if (userData.userType !== 'billingAdmin' && userData.userType !== 'clinicAdmin' && !isBillerWithAdminRole) {
    history.replace('/dashboard');
    return null;
  }

  const clearAll = () => {
    setSearchString('');
    setClearFiltersTrigger(t => t + 1);
  };

  return (
    <>
      <PageTitle title='Billing Admin' />
      <ContainerRow gutter={[0, 40]}>
        <TitleRow justify='space-between'>
          <CustomTypography color={theme['@text-color']}>
            Billing Admin
          </CustomTypography>
          <ControlsDiV style={{ gap: 8 }}>
            {showClinicPicker && (
              <Select
                placeholder='Select Clinic'
                value={clinicId}
                onChange={val => setClinicId(val)}
                style={{ minWidth: 180 }}
                size='middle'
              >
                {billerAdminClinics.map(c => (
                  <Option key={c.clinicId} value={c.clinicId}>
                    {c.clinicName || c.clinicId}
                  </Option>
                ))}
              </Select>
            )}
            <RangePicker
              value={dateRange}
              onChange={val => setDateRange(val || [moment().subtract(60, 'days'), moment()])}
              placeholder={['From', 'To']}
              size='middle'
              allowClear={false}
            />
            <SearchInputStyle
              placeholder='Search'
              prefix={<SearchOutlined />}
              value={searchString}
              onChange={e => setSearchString(e.target.value)}
            />
            <AddButton
              icon={<SettingOutlined />}
              onClick={() => setColumnSettingsVisible(true)}
            >
              Columns
            </AddButton>
            {Object.values(rowSelection).some(Boolean) && (
              <AddButton
                onClick={() => setBulkModalVisible(true)}
                disabled={eligibleJobs.length === 0}
              >
                Reassign Selected ({eligibleJobs.length})
              </AddButton>
            )}
            <AddButton onClick={clearAll}>Clear Filter</AddButton>
          </ControlsDiV>
        </TitleRow>

        {clinicRequired && (
          <div style={{ padding: '16px', color: '#47A4EA', fontWeight: 500 }}>
            Please select a clinic above to view jobs.
          </div>
        )}

        <TanStackUserTable
          data={jobs}
          columns={columns}
          loading={loading}
          searchValue={searchString}
          storageKey='billing_admin_jobs'
          clearFiltersTrigger={clearFiltersTrigger}
          columnVisibility={visibleColumns}
          enableRowSelection
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          getRowId={row => row.jobId}
        />
      </ContainerRow>

      <BillingAdminColumnSettingsModal
        visible={columnSettingsVisible}
        onCancel={() => setColumnSettingsVisible(false)}
        onSave={handleSaveColumnSettings}
        visibleColumns={visibleColumns}
      />

      <ReassignModal
        visible={reassignModalVisible}
        job={reassignTarget}
        billersList={billersList}
        groupsList={groupsList}
        onSubmit={handleReassignSubmit}
        onCancel={() => {
          setReassignModalVisible(false);
          setReassignTarget(null);
        }}
        loading={reassignLoading}
      />

      <BulkReassignModal
        visible={bulkModalVisible}
        eligibleCount={eligibleJobs.length}
        billedCount={selectedJobs.length - eligibleJobs.length}
        billersList={billersList}
        groupsList={groupsList}
        onSubmit={handleBulkReassignSubmit}
        onCancel={() => setBulkModalVisible(false)}
        loading={reassignLoading}
      />
    </>
  );
};

export default BillingAdmin;
